import { useMutation, useLazyQuery, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { createContext, useContext, useMemo, useState } from 'react';
import { UserContext } from '../App';
import { useClientUser } from '@/hooks';
import { COUNT_TAG_USAGE } from './graphql';
import { GET_ORG_TAGS } from '@/graphql/queries/tags';
import { UPSERT_TAGS, DELETE_TAG, REPLACE_ALWAYS_APPLY_CLIENTS } from '@/components/Account/CustomTags/graphql';
import { GET_CORP_PRICING_OVERRIDES, GET_CORP_INTEGRATION_OVERRIDES } from '@/components/CorporateUsers/graphql';
import { buildCarrierAssetMaps, MARKETPLACE_KEY } from '@/components/CorporateUsers/utils';

export const Context = createContext();

const NEW_TAG_DEFAULTS = { route_tag: false, accessorial_tag: false, item_tag: false };
const byBusinessName = (a, b) => (a.business_name || '').localeCompare(b.business_name || '');
const byTag = (a, b) => (a.tag || '').localeCompare(b.tag || '');
const stripTag = ({ __typename, client, always_apply_clients, ...rest }) => rest;

export const ContextProvider = ({ children }) => {
    const { admin_clients, org_tags } = useContext(UserContext);
    const { organization_id } = useClientUser();
    const [notification, setNotification] = useState({});
    const [pendingDelete, setPendingDelete] = useState(null);
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(false);

    const orgTags = useMemo(
        () => [...(org_tags || [])].sort(byTag).map((t) => edits[t.tag_id] || t),
        [org_tags, edits]
    );

    const tagsByClient = useMemo(() => {
        const map = {};
        (admin_clients || []).forEach((client) => {
            map[client.client_id] = [...(client.tags || [])].sort(byTag).map((t) => edits[t.tag_id] || t);
        });
        return map;
    }, [admin_clients, edits]);

    const shipperClients = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).sort(byBusinessName),
        [admin_clients]
    );
    const carrierClients = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['CARRIER']).sort(byBusinessName),
        [admin_clients]
    );

    // Org shipper ids drive the network-carrier lookup, scoped the same way the
    // Accounts page (CorporateUsers) scopes its override queries.
    const orgShipperIds = useMemo(() => shipperClients.map((c) => c.client_id), [shipperClients]);

    const onOverrideError = (e) => {
        captureException(e);
        setNotification({ severity: 'error', message: `Error loading network carriers: ${e.message}` });
    };

    const { data: pricingData } = useQuery(GET_CORP_PRICING_OVERRIDES, {
        variables: { orgShipperIds },
        skip: orgShipperIds.length === 0,
        onError: onOverrideError,
    });

    const { data: integrationData } = useQuery(GET_CORP_INTEGRATION_OVERRIDES, {
        variables: { orgShipperIds },
        skip: orgShipperIds.length === 0,
        onError: onOverrideError,
    });

    // Flat, de-duped list of the org's shipper, carrier, partner accounts, used as
    // options for an org tag's "always apply" account multiselect.
    const accountOptions = useMemo(() => {
        const map = {};
        [...shipperClients, ...carrierClients].forEach((c) => {
            if (map[c.client_id]) return;
            const roles = [];
            if (c.user?.roles?.['SHIPPER']) roles.push('Shipper');
            if (c.user?.roles?.['CARRIER']) roles.push('Carrier');
            map[c.client_id] = {
                client_id: c.client_id,
                label: c.business_name || c.email || c.client_id,
                roles: roles.join(' / '),
                organization_id: null,
            };
        });

        const partners = {};
        if (organization_id) {
            const ownIds = new Set((admin_clients || []).map((c) => c.client_id));

            // Partner carriers
            (admin_clients || []).forEach((client) => {
                (client.shipper_partnerships || []).forEach((partnership) => {
                    const carrier = partnership.carrier;
                    const cid = carrier?.client_id;
                    if (!cid || ownIds.has(cid) || map[cid] || partners[cid]) return;
                    partners[cid] = {
                        client_id: cid,
                        label: carrier.business_name || carrier.email || cid,
                        roles: 'Partner Carrier',
                        organization_id,
                    };
                });
            });

            // Network Carriers
            const { assetLight } = buildCarrierAssetMaps({
                integrationOverrides: integrationData?.integration_overrides,
                pricingOverrides: pricingData?.pricing_overrides,
                adminClients: admin_clients,
            });
            Object.entries(assetLight).forEach(([cid, entry]) => {
                if (cid === MARKETPLACE_KEY || map[cid] || partners[cid]) return;
                const carrier = entry?.carrier;
                partners[cid] = {
                    client_id: cid,
                    label: carrier?.business_name || carrier?.email || cid,
                    roles: 'Network Carrier',
                    organization_id,
                };
            });
        }

        return [...Object.values(map), ...Object.values(partners)];
    }, [shipperClients, carrierClients, admin_clients, organization_id, pricingData, integrationData]);

    const [upsertTags] = useMutation(UPSERT_TAGS, {
        onError: (e) => {
            captureException(e);
            setNotification({ severity: 'error', message: `Error saving tag: ${e.message}` });
        },
        update: (cache, { data: result }) => {
            (result?.insert_tags?.returning || []).forEach((tag) => {
                if (tag.client_id) {
                    cache.modify({
                        id: cache.identify({ __typename: 'clients', client_id: tag.client_id }),
                        fields: {
                            tags(existing = [], { readField, toReference }) {
                                if (existing.some((ref) => readField('tag_id', ref) === tag.tag_id)) return existing;
                                return [...existing, toReference(tag)];
                            },
                        },
                    });
                } else if (tag.organization_id) {
                    cache.updateQuery(
                        { query: GET_ORG_TAGS, variables: { organization_id: tag.organization_id } },
                        (prev) => {
                            const list = prev?.tags || [];
                            if (list.some((t) => t.tag_id === tag.tag_id)) return prev;
                            return { tags: [...list, tag] };
                        }
                    );
                }
            });
        },
    });

    const [replaceAlwaysApply] = useMutation(REPLACE_ALWAYS_APPLY_CLIENTS, {
        refetchQueries: [GET_ORG_TAGS],
        awaitRefetchQueries: true,
        onError: (e) => {
            captureException(e);
            setNotification({ severity: 'error', message: `Error saving always-apply accounts: ${e.message}` });
        },
    });

    const [deleteTag] = useMutation(DELETE_TAG, {
        onError: (e) => {
            captureException(e);
            setNotification({ severity: 'error', message: `Error deleting tag: ${e.message}` });
        },
        update: (cache, { data: result }) => {
            const removedId = result?.delete_tags_by_pk?.tag_id;
            if (!removedId) return;
            cache.evict({ id: cache.identify({ __typename: 'tags', tag_id: removedId }) });
            cache.gc();
        },
    });

    const [fetchUsage] = useLazyQuery(COUNT_TAG_USAGE, {
        fetchPolicy: 'network-only',
        onError: (e) => captureException(e),
    });

    const stageEdit = (tag) => setEdits((prev) => ({ ...prev, [tag.tag_id]: tag }));

    const dropEdit = (tag_id) =>
        setEdits((prev) => {
            if (!prev[tag_id]) return prev;
            const next = { ...prev };
            delete next[tag_id];
            return next;
        });

    const saveEdits = async () => {
        const dirty = Object.values(edits);
        if (!dirty.length) return;
        setSaving(true);
        let ok = false;
        try {
            const res = await upsertTags({ variables: { tags: dirty.map(stripTag) } });
            ok = !!res?.data && !res?.errors?.length;
            if (ok) {
                // Replace each edited tag's always-apply membership (delete + insert).
                const tag_ids = dirty.map((t) => t.tag_id);
                const rows = dirty.flatMap((t) =>
                    (t.always_apply_clients || []).map((c) => ({
                        tag_id: t.tag_id,
                        client_id: c.client_id,
                        organization_id: c.organization_id || null,
                    }))
                );
                const replaceRes = await replaceAlwaysApply({ variables: { tag_ids, rows } });
                ok = !!replaceRes?.data && !replaceRes?.errors?.length;
            }
        } catch (e) {
            ok = false; // the mutation's onError already surfaced the message
        }
        setSaving(false);
        if (ok) {
            setEdits({});
            setNotification({ severity: 'success', message: 'Tags saved' });
        }
    };

    return (
        <Context.Provider
            value={{
                state: {
                    orgTags,
                    tagsByClient,
                    shipperClients,
                    carrierClients,
                    accountOptions,
                    organization_id,
                    notification,
                    pendingDelete,
                    hasEdits: Object.keys(edits).length > 0,
                    saving,
                },
                callbacks: {
                    setNotification,
                    createOrgTag: () => {
                        if (!organization_id) return;
                        upsertTags({
                            variables: {
                                tags: [
                                    {
                                        organization_id,
                                        client_id: null,
                                        tag: `New Org Tag ${orgTags.length + 1}`,
                                        ...NEW_TAG_DEFAULTS,
                                    },
                                ],
                            },
                        });
                    },
                    createClientTag: (client_id) =>
                        upsertTags({
                            variables: {
                                tags: [
                                    {
                                        client_id,
                                        tag: `New Tag ${(tagsByClient[client_id] || []).length + 1}`,
                                        ...NEW_TAG_DEFAULTS,
                                    },
                                ],
                            },
                        }),
                    stageEdit,
                    saveEdits,
                    requestDelete: async (tag) => {
                        let count = 0;
                        try {
                            const { data: usage } = await fetchUsage({ variables: { tag_id: tag.tag_id } });
                            count = usage?.tag_mappings_aggregate?.aggregate?.count || 0;
                        } catch (e) {
                            // fall through — show the modal with count 0 rather than blocking delete
                        }
                        setPendingDelete({ tag, count });
                    },
                    cancelDelete: () => setPendingDelete(null),
                    confirmDelete: () => {
                        const tag = pendingDelete?.tag;
                        if (tag) {
                            deleteTag({ variables: { tag_id: tag.tag_id } });
                            dropEdit(tag.tag_id); // discard any staged edit for the removed tag
                        }
                        setPendingDelete(null);
                    },
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};
